import React from "react";
import { Trophy, LineChart, AlertTriangle, Target, Clock } from "lucide-react";
import { PageHeader } from "../components/layout/Shell";
import { Badge } from "../components/ui/badge";
import { useInstitution } from "../context/InstitutionContext";

/**
 * COMMAND — University Command Centre
 *
 * UI shell preserved. Backend API not yet connected.
 * Three planned signals: enrolment forecast, anomaly alerts, composite readiness.
 * Status: BACKEND_MISSING — /api/command/* endpoints not yet implemented.
 * Tracked in: ACADEMIAOS_MASTER_GAP_ANALYSIS_V1.md · GAP-P0-001
 */

function PlannedPanel({ icon: Icon, title, description, testid }) {
  return (
    <div
      className="rounded-lg border border-dashed border-border bg-muted/30 p-6 flex flex-col gap-3"
      data-testid={testid}
    >
      <div className="flex items-center gap-2 text-muted-foreground">
        <Icon className="h-4 w-4 shrink-0" />
        <span className="text-xs font-semibold uppercase tracking-wider">{title}</span>
      </div>
      <p className="text-sm text-muted-foreground">{description}</p>
      <Badge variant="outline" className="self-start text-[10px] text-muted-foreground border-border">
        <Clock className="h-2.5 w-2.5 me-1" /> Not yet connected
      </Badge>
    </div>
  );
}

export default function CommandCentre() {
  const { current } = useInstitution();
  if (!current) return null;

  return (
    <div data-testid="command-page">
      <PageHeader
        eyebrow="COMMAND · University Command Centre"
        title={`${current.short_name} · Strategic Command`}
        description="One leadership cockpit across admissions, student success, academics, finance, workforce, research, and quality. Backend integration in progress."
        actions={
          <Badge variant="outline" className="gap-1.5 text-muted-foreground">
            <Clock className="h-3 w-3" /> Backend not yet connected
          </Badge>
        }
      />

      <div className="p-6 lg:p-8 space-y-6">
        <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800 p-4 text-sm text-amber-800 dark:text-amber-300" data-testid="command-not-connected-notice">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
            <div>
              <div className="font-semibold">University Command Centre — Backend not yet connected</div>
              <div className="mt-1 text-xs opacity-80">
                The Command Centre requires a canonical operational data pipeline across all domain services.
                The UI structure is ready. Backend integration is planned under Wave I4 (University Command Centre).
                All other AcademiaOS modules continue to function normally.
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <PlannedPanel
            icon={LineChart}
            title="Predictive Enrolment"
            description="Linear regression on historical admission cycles to project the next 3 cohort sizes. Requires canonical Student + Admissions operational data."
            testid="cmd-forecast-panel"
          />
          <PlannedPanel
            icon={AlertTriangle}
            title="Anomaly Alerts"
            description="Cross-domain KPI anomaly detection — placement rates, at-risk counts, audit backlogs, and HITL queues. Requires operational domain APIs."
            testid="cmd-anomalies-panel"
          />
          <PlannedPanel
            icon={Trophy}
            title="Composite Readiness"
            description="NIRF-style 5-dimension institutional readiness score with dimensional breakdown and grade projection. Requires verified canonical data sources."
            testid="cmd-readiness-panel"
          />
        </div>

        <div className="rounded-lg border border-border bg-card p-5">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1.5">
            <Target className="h-3.5 w-3.5" /> Planned Coverage
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
            {[
              "Admissions", "Student Success", "Academics",
              "Finance", "Workforce", "Research", "Quality",
              "Campus Operations", "AI Governance", "ARGOV",
            ].map((domain) => (
              <div
                key={domain}
                className="rounded border border-dashed border-border px-3 py-2 text-xs text-muted-foreground text-center"
                data-testid={`cmd-domain-${domain.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {domain}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
